import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Check, List, X } from 'lucide-react';
import { OptionKey, Question } from '../data/types';

interface QuizMobileNavProps {
  day: number;
  currentPos: number;
  totalCount: number;
  answeredCount: number;
  questions: Question[];
  answers: Record<number, OptionKey>;
  flagged: number[];
  currentIndex: number;
  isFirst: boolean;
  isLast: boolean;
  isLocked: boolean;
  isSaving?: boolean;
  lockedQuestionIds?: number[];
  canSubmit: boolean;
  onPrev: () => void;
  onNext: () => void;
  onSelectQuestion: (index: number) => void;
  onOpenSubmitModal: () => void;
}

export const QuizMobileNav: React.FC<QuizMobileNavProps> = ({
  day,
  currentPos,
  totalCount,
  answeredCount,
  questions,
  answers,
  flagged,
  currentIndex,
  isFirst,
  isLast,
  isLocked,
  isSaving = false,
  lockedQuestionIds = [],
  canSubmit,
  onPrev,
  onNext,
  onSelectQuestion,
  onOpenSubmitModal,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const activeItemRef = useRef<HTMLButtonElement | null>(null);

  // Auto-scroll active question in the quick horizontal strip into center view
  useEffect(() => {
    if (activeItemRef.current) {
      activeItemRef.current.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest',
      });
    }
  }, [currentIndex]);

  return (
    <>
      {/* Top horizontal quick scroll list */}
      <div className="fc-mobile-strip">
        <button
          type="button"
          className="fc-mobile-list-btn"
          onClick={() => setDrawerOpen(true)}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <List size={14} /> Danh sách
          </span>
        </button>

        <div className="fc-mobile-scroll-track">
          {questions.map((q, idx) => {
            const hasAnswer = Boolean(answers[q.id]);
            const isQuestionLocked = lockedQuestionIds.includes(q.id);
            const isCompleted = isLocked ? hasAnswer : isQuestionLocked;
            const isAnswered = !isCompleted && hasAnswer;
            const isCurrent = idx === currentIndex;
            const isItemFlagged = flagged.includes(q.id);

            return (
              <button
                key={q.id}
                ref={isCurrent ? activeItemRef : undefined}
                type="button"
                className={`fc-mobile-grid-item ${isCompleted ? 'completed' : isAnswered ? 'answered' : ''} ${isCurrent ? 'current' : ''}`}
                onClick={() => {
                  if (!isSaving) onSelectQuestion(idx);
                }}
                disabled={isSaving}
                title={`Câu ${idx + 1}${isCompleted ? ' (Đã hoàn thành)' : hasAnswer ? ' (Đã làm)' : ' (Chưa làm)'}`}
              >
                <span>{idx + 1}</span>
                {isCompleted && (
                  <span className="fc-completed-badge">
                    <Check size={8} strokeWidth={3} />
                  </span>
                )}
                {isItemFlagged && <span className="fc-flag-dot" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Slide-up Drawer Modal */}
      {drawerOpen && (
        <>
          <div
            className="fc-drawer-overlay"
            onClick={() => setDrawerOpen(false)}
          />
          <div className="fc-drawer-sheet">
            <div className="fc-drawer-handle" />
            <div className="fc-drawer-header">
              <div className="fc-drawer-title">
                Ngày {day} · {answeredCount}/{totalCount} đã trả lời
              </div>
              <button
                type="button"
                className="fc-drawer-close"
                onClick={() => setDrawerOpen(false)}
                title="Đóng"
              >
                <X size={16} />
              </button>
            </div>

            {/* Color Legend inside Drawer for Mobile */}
            <div className="fc-drawer-legend">
              <div className="fc-drawer-legend-item">
                <span className="fc-dlg-box comp" />
                <span>Hoàn thành</span>
              </div>
              <div className="fc-drawer-legend-item">
                <span className="fc-dlg-box ans" />
                <span>Đã làm</span>
              </div>
              <div className="fc-drawer-legend-item">
                <span className="fc-dlg-box cur" />
                <span>Hiện tại</span>
              </div>
              <div className="fc-drawer-legend-item">
                <span className="fc-dlg-box flg" />
                <span>Đánh dấu</span>
              </div>
            </div>

            <div className="fc-drawer-grid">
              {questions.map((q, idx) => {
                const hasAnswer = Boolean(answers[q.id]);
                const isQuestionLocked = lockedQuestionIds.includes(q.id);
                const isCompleted = isLocked ? hasAnswer : isQuestionLocked;
                const isAnswered = !isCompleted && hasAnswer;
                const isCurrent = idx === currentIndex;
                const isItemFlagged = flagged.includes(q.id);

                return (
                  <button
                    key={q.id}
                    type="button"
                    className={`fc-drawer-grid-item ${isCompleted ? 'completed' : isAnswered ? 'answered' : ''} ${isCurrent ? 'current' : ''}`}
                    onClick={() => {
                      if (!isSaving) {
                        onSelectQuestion(idx);
                        setDrawerOpen(false);
                      }
                    }}
                    disabled={isSaving}
                    title={`Câu ${idx + 1}${isCompleted ? ' (Đã hoàn thành)' : hasAnswer ? ' (Đã làm)' : ' (Chưa làm)'}`}
                  >
                    <span>{idx + 1}</span>
                    {isCompleted && (
                      <span className="fc-completed-badge">
                        <Check size={8} strokeWidth={3} />
                      </span>
                    )}
                    {isItemFlagged && <span className="fc-flag-dot" />}
                  </button>
                );
              })}
            </div>

            {canSubmit && (
              <button
                type="button"
                className="fc-drawer-finish-btn"
                onClick={() => {
                  setDrawerOpen(false);
                  onOpenSubmitModal();
                }}
                disabled={isSaving}
              >
                Hoàn thành Ngày {day}
              </button>
            )}
          </div>
        </>
      )}

      {/* Sticky Bottom Navigation */}
      <div className="fc-mobile-bottom-bar">
        <button
          type="button"
          className="fc-m-btn prev"
          onClick={onPrev}
          disabled={isFirst || isSaving}
        >
          <ArrowLeft size={16} style={{ marginRight: 4 }} /> Trước
        </button>

        <div className="fc-m-counter">
          {currentPos} / {totalCount}
        </div>

        {!isLast ? (
          <button
            type="button"
            className="fc-m-btn next"
            onClick={onNext}
            disabled={isSaving}
          >
            Sau <ArrowRight size={16} style={{ marginLeft: 4 }} />
          </button>
        ) : !isLocked ? (
          <button
            type="button"
            className="fc-m-btn finish"
            onClick={onOpenSubmitModal}
            disabled={isSaving}
          >
            Hoàn thành
          </button>
        ) : (
          <button
            type="button"
            className="fc-m-btn finish"
            disabled
            style={{ opacity: 0.6, cursor: 'default' }}
          >
            Đã nộp bài
          </button>
        )}
      </div>
    </>
  );
};

